import { Axial } from '../game/map/HexCoordinates';

/**
 * Drogi po drucie.
 *
 * Podróżują jak budynki, nie jak teren: **co jest w obszarze** przychodzi po
 * HTTP razem z ziemią, a **co się właśnie zmieniło** — socketem. Powód jest ten
 * sam, dla którego budynki tak podróżują: to jest stan gry, który powstaje
 * w trakcie, a nie warstwa świata, która jest stała.
 */

const API = import.meta.env.VITE_API_URL ?? '';

export interface RoadCell extends Axial {
  /** Bity 0–5: przez które krawędzie droga przechodzi. */
  mask: number;
  owner: string;
  /** Sekundy do gotowości, zero na gotowej. Odliczane zegarem klienta. */
  readyIn: number;
}

/** Co poszło na jedno zamówienie drogi. */
export interface Laid {
  cells: RoadCell[];
  paid: number;
  material: string;
}

export interface Refused {
  code: string;
  message: string;
}

/** Co biegnie w promieniu tylu pierścieni chunków od pola. */
export async function loadRoads(at: Axial, radius: number): Promise<RoadCell[]> {
  const response = await fetch(
    `${API}/api/world/roads/at?q=${at.q}&r=${at.r}&radius=${radius}`
  );
  if (!response.ok) {
    throw new Error(`drogi: ${response.status}`);
  }
  return (await response.json()) as RoadCell[];
}

/**
 * Połóż drogę wzdłuż podanej trasy — z pierwszym polem włącznie.
 *
 * **Początek jedzie w żądaniu**, bo nie zawsze jest nim pole pod graczem:
 * kolejny odcinek na SHIFT-cie zaczyna się tam, gdzie skończył się poprzedni,
 * a postać zostaje na miejscu. Serwer sprawdza ten początek po swojemu —
 * przyjmuje pole gracza albo takie, na którym droga już jest — więc reguła
 * „nie kładziemy drogi tam, gdzie nikogo nie ma i nic nie ma" zostaje jego.
 *
 * **Trasa jedzie cała, a nie sam jej koniec.** Serwer szukał jej wcześniej sam
 * i na płaskim terenie — gdzie kilkanaście tras jest równie szybkich — wybierał
 * inną niż ta narysowana pod kursorem. Dwóch wyszukiwań nie da się zmusić do
 * zgody co do tego, którą z równych tras wybrać; da się je zmusić do zgody co
 * do tego, czy trasa jest dozwolona. Więc wybiera gracz, a serwer sprawdza
 * każdy krok: sąsiedztwo, przejezdność, brak biegu wzdłuż rzeki i długość.
 */
export async function buildRoad(token: string, cells: Axial[]): Promise<Laid | Refused> {
  const response = await fetch(`${API}/api/world/roads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ cells: cells.map((cell) => ({ q: cell.q, r: cell.r })) }),
  });

  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as Laid) : (body as Refused);
}

/**
 * Postaw most na polu wody, w osi 0–2 (krawędź `axis` i przeciwległa).
 * Odpowiedź ma kształt odpowiedzi drogi: pola, które się zmieniły, i cenę.
 */
export async function buildBridge(
  token: string,
  cell: Axial,
  axis: number
): Promise<Laid | Refused> {
  const response = await fetch(`${API}/api/world/bridges`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ q: cell.q, r: cell.r, axis }),
  });
  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as Laid) : (body as Refused);
}
